import React, { useEffect, useState } from 'react';
import { Sparkles, X, Clock } from 'lucide-react';
import axios from 'axios';
import './atom.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * "Watch what just happened" — Adamu 8-second animated vertical timeline.
 * Trigger from the wallet page. Reads the latest salary loop's ledger rows.
 */
export default function AdamuTimeline({ csId, open, onClose }) {
  const [data, setData] = useState(null);
  const [visibleSteps, setVisibleSteps] = useState(0);

  useEffect(() => {
    if (!open || !csId) return;
    setData(null);
    setVisibleSteps(0);
    axios.get(`${API}/api/demo/gdl/timeline/salary/${csId}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setData({ steps: [], available: false }));
  }, [open, csId]);

  // Stagger the step reveal: ~750ms per step → 8 steps in ~6 s.
  useEffect(() => {
    if (!data?.steps?.length) return;
    setVisibleSteps(0);
    const ids = [];
    for (let i = 0; i < data.steps.length; i++) {
      ids.push(setTimeout(() => setVisibleSteps(v => i + 1), 200 + i * 750));
    }
    return () => ids.forEach(clearTimeout);
  }, [data]);

  if (!open) return null;

  return (
    <div onClick={onClose} data-testid="adamu-timeline-overlay" style={{
      position: 'fixed', inset: 0,
      background: 'rgba(13,3,5,0.78)',
      animation: 'gdlBackdropIn 0.2s ease',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 60,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 360, maxHeight: '88vh', overflowY: 'auto',
        background: '#fff', borderRadius: 24,
        padding: '22px 20px 24px',
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        animation: 'gdlModalIn 0.35s ease',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, #B03140, #8E2233)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(176,49,64,0.3)',
          }}>
            <Clock size={18} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                            color: '#8E2233', textTransform: 'uppercase' }}>
              Behind the screen
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#2D0A10' }}>
              {data?.civil_servant_name
                ? `${data.civil_servant_name.split(' ')[0]}'s 8-second`
                : 'Salary'} loop
            </div>
          </div>
          <button onClick={onClose} data-testid="adamu-close" style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: '#A07078', padding: 6,
          }}><X size={20}/></button>
        </div>

        {!data && (
          <div style={{ padding: 40, textAlign: 'center', color: '#A07078' }}>
            Loading…
          </div>
        )}

        {data && !data.available && (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#2D0A10', marginBottom: 6 }}>
              No salary loop yet
            </div>
            <div style={{ fontSize: 13, color: '#A07078' }}>
              Run "Simulate salary" from the admin panel first, then check back.
            </div>
          </div>
        )}

        {data?.available && (
          <>
            {/* Summary header */}
            <div style={{
              background: 'linear-gradient(145deg, #C43B4D, #B03140)',
              borderRadius: 16, padding: '12px 14px',
              marginBottom: 18, color: '#fff',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 600, textTransform: 'uppercase' }}>
                    Salary credited
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>
                    {'\u20A6'}{Math.round(data.salary_amount).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 600, textTransform: 'uppercase' }}>
                    Ring-fenced
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#FFD7DC' }}>
                    {'\u20A6'}{Math.round(data.deduction_amount).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 600, textTransform: 'uppercase' }}>
                    Net to user
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#A7F3D0' }}>
                    {'\u20A6'}{Math.round(data.final_balance).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div style={{ position: 'relative', paddingLeft: 28 }}>
              {/* Vertical line */}
              <div style={{
                position: 'absolute', left: 11, top: 8, bottom: 8,
                width: 2, background: 'rgba(176,49,64,0.15)', borderRadius: 1,
              }}>
                <div style={{
                  width: '100%',
                  height: `${(visibleSteps / data.steps.length) * 100}%`,
                  background: 'linear-gradient(180deg, #B03140, #8E2233)',
                  borderRadius: 1, transition: 'height 0.4s ease',
                }} />
              </div>

              {data.steps.map((s, i) => (
                <div key={i} data-testid={`adamu-step-${i}`} style={{
                  position: 'relative', marginBottom: 16,
                  opacity: i < visibleSteps ? 1 : 0,
                  transform: i < visibleSteps ? 'translateX(0)' : 'translateX(-6px)',
                  transition: 'all 0.35s ease',
                }}>
                  {/* Node */}
                  <div style={{
                    position: 'absolute', left: -24, top: 4,
                    width: 18, height: 18, borderRadius: '50%',
                    background: i < visibleSteps ? '#B03140' : '#fff',
                    border: `2px solid ${i < visibleSteps ? '#8E2233' : 'rgba(176,49,64,0.3)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: i < visibleSteps ? '0 2px 6px rgba(176,49,64,0.3)' : 'none',
                    animation: i < visibleSteps ? 'gdlTimelineNode 0.4s ease' : 'none',
                  }}>
                    {i < visibleSteps && <Sparkles size={9} color="#fff" />}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                  alignItems: 'baseline', marginBottom: 2, gap: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#2D0A10' }}>
                      {s.label}
                    </div>
                    <div style={{ fontSize: 10, fontFamily: 'monospace',
                                    color: '#A07078', flexShrink: 0 }}>
                      {s.time}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#5A2A33', lineHeight: 1.45,
                                  marginBottom: 4 }}>
                    {s.detail}
                  </div>
                  <div style={{
                    display: 'inline-block', fontSize: 10, fontWeight: 700,
                    padding: '2px 8px', borderRadius: 999,
                    background: 'rgba(176,49,64,0.08)', color: '#8E2233',
                  }}>
                    {s.particle}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
